<template>
  <Card class="w-full max-w-md mx-auto">
    <CardHeader>
      <CardTitle>{{ $t('auth.forgot-password.title') }}</CardTitle>
      <CardDescription v-html="$t('auth.forgot-password.text')" />
    </CardHeader>

    <CardContent>
      <div class="space-y-4">
        <Label for="email">{{ $t('auth.forgot-password.fields.email.title') }}</Label>
        <Input
            id="email"
            type="email"
            v-model="email"
            placeholder="example@gmail.com"
            :disabled="isDataLoading"

            :class="{
              'border-red-500': emailErrors.length > 0 && isTouched,
              'focus:outline-none focus-visible:ring-0 focus-visible:border-black': true
            }"

            @blur="isTouched = true"
        />
        <span v-if="emailErrors.length > 0 && isTouched" class="text-sm text-red-500">
          {{ emailErrorsFormatted.map(error => $t(error.$message)).join(', ') }}
        </span>
      </div>
    </CardContent>

    <CardFooter>
<!--      @click="sendForgotPasswordLink"-->
      <Button
          class="w-full bg-[#64748B] transition-colors hover:bg-[#64748B] hover:opacity-75 toast-success"
          :disabled="isDataLoading || !isEmailValid"
          @click="sendForgotPasswordLink"
      >
        {{ $t('auth.forgot-password.actions.reset') }}
      </Button>
    </CardFooter>
  </Card>

<!--  <SuccessNotification-->
<!--      ref="forgotLinkSentNotification"-->
<!--      :text="$t('auth.forgot-password.notifications.forgot-link-sent')"-->
<!--  />-->
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { toast } from "vue-sonner";
import { getCurrentInstance } from 'vue';
import { UserApi } from '@js-app/office/api/user-api'

const email = ref('')
const isDataLoading = ref(false)
const forgotLinkSentNotification = ref(null)
const instance = getCurrentInstance()
const t = (key: string) => instance?.ctx.$i18n.t(key)



const isTouched = ref(false)
const isValidEmailFormat = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(value)
}

const emailErrors = computed<string[]>(() => {
  const errors: string[] = []

  if (!email.value.trim()) {
    errors.push('validation.required')
  } else if (!isValidEmailFormat(email.value)) {
    errors.push('validation.email')
  }

  return errors
})

const isEmailValid = computed<boolean>(() => emailErrors.value.length === 0)

const emailErrorsFormatted = computed(() => {
  const messages: Record<string, string> = {
    'validation.required': 'cabinet-forgot-password.errors.validation_required',
    'validation.email': 'cabinet-forgot-password.errors.validation_email',
  };

  return emailErrors.value.map(key => ({
    $message: messages[key] || `${key}`,
    $validator: key,
  }));
});

const sendForgotPasswordLink = async () => {
  isTouched.value = true

  if (!isEmailValid.value) {
    return
  }

  isDataLoading.value = true
  try {
    const response = await UserApi.sendForgotPasswordLink(email.value);
    if (response && response.data.success) {
      toast(t('cabinet-forgot-password.toast.check_email_title'), {
        description: t('cabinet-forgot-password.toast.check_email_text'),
        duration: 5000,
        closeButton: true,
      });

      setTimeout(() => {
        window.location.href = '/login'
      }, 5000);
    } else {
      toast.error(t('auth.forgot-password.notifications.forgot-link-error'))
    }

  } catch (e) {} finally {
    isDataLoading.value = false
  }
}
</script>

<style scoped>
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>
